import React from "react";
import { useNavigate } from "react-router-dom";

const Hero = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      id: 1,
      text: "Enterprise-grade cloud infrastructure with 99.9% uptime",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      )
    },
    {
      id: 2,
      text: "Intelligent scheduling & comprehensive patient care",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 3,
      text: "Advanced analytics & automated financial workflows",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 4,
      text: "Enterprise-grade security & HIPAA compliance",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }
  ];

  const handleScheduleDemo = () => {
    navigate('/schedule-demo');
  };

  const handleComparePlans = () => {
    navigate('/pricing');
  };

  return (
    <section className="relative pt-40 md:pt-48 pb-16 md:pb-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
          {/* Left Content */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Modern Dental Practice Management
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
              Transform your practice with enterprise-grade technology. Eliminate hardware costs, streamline operations, and enhance patient care with our comprehensive cloud solution.
            </p>

            {/* Benefits List */}
            <div className="space-y-4 mb-8">
              {benefits.map((benefit) => (
                <div key={benefit.id} className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                    {benefit.icon}
                  </div>
                  <span className="text-gray-700 font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              {/* Schedule Demo Button */}
              <button 
                onClick={handleScheduleDemo}
                className="group relative px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-orange-600 to-orange-400 rounded-xl md:rounded-2xl text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                {/* Animated background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Button content with icon */}
                <div className="relative flex items-center justify-center space-x-2 text-white">
                  <span>Schedule a Demo</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </button>

              {/* Compare Plans Button */}
              <button 
                onClick={handleComparePlans}
                className="group relative px-6 md:px-8 py-3 md:py-4 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl md:rounded-2xl"></div>
                <div className="relative flex items-center justify-center space-x-2 text-gray-800">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Compare Plans</span>
                </div>
              </button>
            </div>
          </div>

          {/* Right Content - Stats Card and Dashboard Preview */}
          <div className="w-full lg:w-1/2 relative mt-8 lg:mt-0">
            {/* Stats Card */}
            <div className="bg-[#001233] text-white rounded-2xl p-6 md:p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300 relative overflow-hidden w-full mb-8">
              {/* Background gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-cyan-400/10"></div>
              
              {/* Content */}
              <div className="relative text-center z-10">
                <h2 className="text-xl md:text-2xl font-bold mb-4 text-white">
                  Proven Business Impact
                </h2>
                <div className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-8 md:mb-12">
                  5x ROI
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
                  {/* Higher Collections */}
                  <div className="group">
                    <div className="text-3xl md:text-4xl lg:text-6xl font-bold text-gray-300 mb-2">
                      20%
                    </div>
                    <p className="text-base md:text-lg text-gray-400 font-medium">
                      Revenue Growth
                    </p>
                  </div>

                  {/* More New Patients */}
                  <div className="group">
                    <div className="text-3xl md:text-4xl lg:text-6xl font-bold text-gray-300 mb-2">
                      25%
                    </div>
                    <p className="text-base md:text-lg text-gray-400 font-medium">
                      Patient Acquisition
                    </p>
                  </div>

                  {/* Better Case Acceptance */}
                  <div className="group">
                    <div className="text-3xl md:text-4xl lg:text-6xl font-bold text-gray-300 mb-2">
                      30%
                    </div>
                    <p className="text-base md:text-lg text-gray-400 font-medium">
                      Treatment Acceptance
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl"></div>
            </div>

            {/* Dashboard Preview */}
            <div className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl overflow-hidden">
              {/* Browser-like Header */}
              <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 bg-white rounded-full px-4 py-1 text-sm text-gray-600">
                    app.vyxdental.com
                  </div>
                </div>
              </div>

              {/* Dashboard Preview */}
              <div className="p-4 md:p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-sm text-gray-600">Cost Savings</div>
                    <div className="text-xl md:text-2xl font-bold text-gray-900">45%</div>
                    <div className="text-xs text-green-600 flex items-center">
                      <span className="mr-1">↓</span> vs. Traditional Software
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-lg p-4">
                    <div className="text-sm text-gray-600">Time Saved</div>
                    <div className="text-xl md:text-2xl font-bold text-gray-900">12hrs</div>
                    <div className="text-xs text-green-600 flex items-center">
                      <span className="mr-1">↑</span> Weekly Efficiency
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-orange-200 rounded-full opacity-20 blur-2xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-orange-300 rounded-full opacity-20 blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 